import { useState, useEffect, useRef } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Badge } from '../ui/Badge'
import { DiscountModal } from './DiscountModal'
import { PriceOverrideModal } from './PriceOverrideModal'
import { ShoppingCart, Trash2, Minus, Plus, Package, Percent, Edit } from 'lucide-react'

interface CartItem {
  id: string
  name: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface MobileCartTableProps {
  cart: CartItem[]
  onUpdateQuantity: (productId: string, quantity: number) => void
  onRemoveFromCart: (productId: string) => void
  onClearCart: () => void
  formatCurrency: (amount: number) => string
  highlightedItems?: Set<string>
  onApplyItemDiscount?: (itemId: string, type: 'fixed' | 'percentage', value: number) => void
  onRemoveItemDiscount?: (itemId: string) => void
  onOverridePrice?: (itemId: string, price: number) => void
}

export const MobileCartTable = ({ 
  cart, 
  onUpdateQuantity, 
  onRemoveFromCart, 
  onClearCart, 
  formatCurrency,
  highlightedItems = new Set(),
  onApplyItemDiscount,
  onRemoveItemDiscount,
  onOverridePrice
}: MobileCartTableProps) => {
  const [showItemDiscountModal, setShowItemDiscountModal] = useState<string | null>(null)
  const [showPriceOverrideModal, setShowPriceOverrideModal] = useState<string | null>(null)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  
  // Auto-scroll to highlighted items
  useEffect(() => {
    if (highlightedItems.size > 0 && containerRef.current) {
      const highlightedItemId = Array.from(highlightedItems)[0]
      const itemElement = itemRefs.current[highlightedItemId]
      
      if (itemElement) {
        itemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }
  }, [highlightedItems])
  
  const currentItemForDiscount = showItemDiscountModal ? cart.find(item => item.id === showItemDiscountModal) : null
  const currentItemForPriceOverride = showPriceOverrideModal ? cart.find(item => item.id === showPriceOverrideModal) : null

  if (cart.length === 0) {
    return (
      <Card className="flex-1 flex flex-col">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Cart (0 items)
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Cart is Empty</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Search and add products to get started</p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className="flex-1 flex flex-col">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" />
            Cart ({cart.length} items)
          </h3>
          <Button variant="outline" size="sm" onClick={onClearCart} className="h-7 px-2 text-xs">
            <Trash2 className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-auto p-2">
        <div className="space-y-3">
          {cart.map((item) => {
            const isHighlighted = highlightedItems.has(item.id)
            return (
              <div
                key={item.id}
                ref={(el) => { itemRefs.current[item.id] = el }}
                className={`p-3 rounded border transition-all duration-500 ${
                  isHighlighted 
                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-600' 
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                {/* Compact Item Row */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                        {item.name}
                      </h4>
                      {item.isWeighable && (
                        <Badge variant="secondary" size="sm" className="text-xs h-4 px-1">
                          {item.unit}
                        </Badge>
                      )}
                      {item.price !== item.originalPrice && (
                        <Badge variant="warning" size="sm" className="text-xs h-4 px-1">
                          Override
                        </Badge>
                      )}
                      {item.discount && (
                        <Badge variant="success" size="sm" className="text-xs h-4 px-1">
                          {item.discount.type === 'percentage' ? `${item.discount.value}%` : `₹${item.discount.value}`}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ₹{item.originalPrice} • GST {item.gst_rate}%
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-2">
                    {/* Action Buttons moved to top row */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowItemDiscountModal(item.id)}
                      className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                      title={item.discount ? "Remove Discount" : "Add Discount"}
                    >
                      <Percent className="h-3 w-3" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPriceOverrideModal(item.id)}
                      className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700"
                      title="Override Price"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveFromCart(item.id)}
                      className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Compact Quantity and Price Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const decrement = item.isWeighable ? 0.1 : 1
                        const minQty = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, Math.max(minQty, item.quantity - decrement))
                      }}
                      className="w-7 h-7 p-0"
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    
                    <Input
                      type="number"
                      min={item.isWeighable ? "0.01" : "1"}
                      step={item.isWeighable ? "0.1" : "1"}
                      value={item.quantity}
                      onChange={(e) => {
                        const qty = parseFloat(e.target.value) || (item.isWeighable ? 0.1 : 1)
                        const minQty = item.isWeighable ? 0.01 : 1
                        onUpdateQuantity(item.id, Math.max(minQty, qty))
                      }}
                      className="w-14 text-center h-7 text-sm"
                    />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const increment = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, item.quantity + increment)
                      }}
                      className="w-7 h-7 p-0"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(item.total)}
                      </div>
                      {item.discount && (
                        <div className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">
                          -{formatCurrency(item.discount.amount)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      
      {/* Modals */}
      <DiscountModal
        isOpen={showItemDiscountModal !== null}
        onClose={() => setShowItemDiscountModal(null)}
        onApply={(type, value) => {
          if (showItemDiscountModal && onApplyItemDiscount) {
            onApplyItemDiscount(showItemDiscountModal, type, value)
          }
          setShowItemDiscountModal(null)
        }}
        title={`Apply Discount - ${currentItemForDiscount?.name || ''}`}
        currentPrice={currentItemForDiscount ? currentItemForDiscount.price * currentItemForDiscount.quantity : undefined}
      />
      
      <PriceOverrideModal
        isOpen={showPriceOverrideModal !== null}
        onClose={() => setShowPriceOverrideModal(null)}
        onApply={(newPrice) => {
          if (showPriceOverrideModal && onOverridePrice) {
            onOverridePrice(showPriceOverrideModal, newPrice)
          }
          setShowPriceOverrideModal(null)
        }}
        itemName={currentItemForPriceOverride?.name || ''}
        originalPrice={currentItemForPriceOverride?.originalPrice || 0}
        currentPrice={currentItemForPriceOverride?.price || 0}
      />
    </Card>
  )
}